import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { copyText } from '@repletoryreach/sdk-utils/clipboard';

export interface CopyButtonRenderProps {
  /** True for `timeout` ms after a successful copy. */
  copied: boolean;
  /** True when the clipboard write failed (permissions, insecure context). */
  failed: boolean;
  copy: () => void;
}

export interface CopyButtonProps {
  /** The text placed on the clipboard. */
  value: string;
  /** How long `copied` stays true. */
  timeout?: number;
  children: (props: CopyButtonRenderProps) => ReactNode;
}

/**
 * Render-prop wrapper around the clipboard, so a "copy this" affordance is one
 * component rather than a `useState` and a `setTimeout` at every call site.
 *
 * Both consoles need this: identifiers that a user has to paste back into
 * another system (a Meta phone-number-id, an API key, a webhook secret) are
 * scattered across both, and each one was previously either uncopyable or a
 * hand-rolled copy of the same three lines.
 *
 * `failed` is surfaced rather than swallowed: the Clipboard API rejects
 * outright in an insecure context, and a button that silently does nothing is
 * worse than one that says so.
 */
export function CopyButton({ value, timeout = 1500, children }: CopyButtonProps): ReactNode {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clearing on unmount keeps the state update off an unmounted component when
  // the button lives in a row that re-renders away.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const copy = useCallback(() => {
    void copyText(value).then((result) => {
      const ok = result.kind === 'ok';
      setCopied(ok);
      setFailed(!ok);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => { setCopied(false); setFailed(false); }, timeout);
    });
  }, [value, timeout]);

  return <>{children({ copied, failed, copy })}</>;
}
